import { useEffect, useState } from "react";
import { useTranslation } from "react-i18next";
import { supabase } from "@/integrations/supabase/client";
import { useAuth } from "@/lib/auth";
import { AppShell } from "@/components/AppShell";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Button } from "@/components/ui/button";
import { Switch } from "@/components/ui/switch";
import { SUPPORTED_LANGUAGES, LangCode } from "@/i18n";
import { toast } from "sonner";
import { APP_THEMES, AppTheme, getStoredTheme, setTheme, applyDefaultThemeIfUnset, fetchDefaultTheme, clearThemeOverride, persistUserTheme } from "@/lib/theme";
import { Check, Loader2 } from "lucide-react";
import { PageMeta } from "@/components/PageMeta";
import { enablePush, disablePush, hasActivePushSubscription, isPushSupported } from "@/lib/webPush";

type EmailPrefs = { invites: boolean; results: boolean; changes: boolean };
const DEFAULT_PREFS: EmailPrefs = { invites: true, results: true, changes: true };

type NotifPrefs = {
  tip_reminder: boolean;
  match_result: boolean;
  invite: boolean;
  rules_change: boolean;
  push_enabled: boolean;
};
const DEFAULT_NOTIF_PREFS: NotifPrefs = {
  tip_reminder: true,
  match_result: true,
  invite: true,
  rules_change: true,
  push_enabled: false,
};

const Profile = () => {
  const { user } = useAuth();
  const { t, i18n } = useTranslation();
  const [name, setName] = useState("");
  const [email, setEmail] = useState("");
  const [prefs, setPrefs] = useState<EmailPrefs>(DEFAULT_PREFS);
  const [notifPrefs, setNotifPrefs] = useState<NotifPrefs>(DEFAULT_NOTIF_PREFS);
  const [themeOverride, setThemeOverride] = useState<AppTheme | null>(getStoredTheme());
  const [defaultTheme, setDefaultThemeState] = useState<AppTheme>("default");
  const [pushBusy, setPushBusy] = useState(false);
  const [pushActive, setPushActive] = useState(false);
  const [testBusy, setTestBusy] = useState(false);
  const pushSupported = isPushSupported();

  useEffect(() => {
    if (!pushSupported) return;
    hasActivePushSubscription().then(setPushActive).catch(() => {});
  }, [pushSupported]);

  const togglePush = async (next: boolean) => {
    if (!user || pushBusy) return;
    if (!pushSupported) { toast.error(t("notifications.pushUnsupported")); return; }
    setPushBusy(true);
    try {
      if (next) {
        await enablePush(user.id);
        setPushActive(true);
        await toggleNotifPref("push_enabled", true);
        toast.success(t("notifications.pushEnabled"));
      } else {
        await disablePush();
        setPushActive(false);
        await toggleNotifPref("push_enabled", false);
        toast.success(t("notifications.pushDisabled"));
      }
    } catch (e: any) {
      const msg = e?.message === "Permission denied" ? t("notifications.pushDenied") : (e?.message ?? "Error");
      toast.error(msg);
    } finally {
      setPushBusy(false);
    }
  };

  const sendTest = async () => {
    if (!user || testBusy) return;
    setTestBusy(true);
    try {
      const { data, error } = await supabase.rpc("send_test_notifications");
      if (error) throw error;
      const n = (data as number) ?? 0;
      if (n === 0) toast.info(t("notifications.testNone"));
      else toast.success(t("notifications.testSent", { count: n }));
    } catch (e: any) {
      toast.error(e?.message ?? "Error");
    } finally {
      setTestBusy(false);
    }
  };

  useEffect(() => {
    fetchDefaultTheme().then(setDefaultThemeState);
  }, []);

  const pickTheme = async (id: AppTheme) => {
    setTheme(id);
    setThemeOverride(id);
    if (user) {
      const { error } = await (async () => {
        try { await persistUserTheme(user.id, id); return { error: null as any }; }
        catch (e: any) { return { error: e }; }
      })();
      if (error) { toast.error(error.message ?? "Error"); return; }
    }
    toast.success(t("profile.themeSaved"));
  };
  const useDefaultTheme = async () => {
    clearThemeOverride();
    setThemeOverride(null);
    await applyDefaultThemeIfUnset();
    if (user) {
      try { await persistUserTheme(user.id, null); }
      catch (e: any) { toast.error(e?.message ?? "Error"); return; }
    }
    toast.success(t("profile.themeSaved"));
  };

  useEffect(() => {
    if (!user) return;
    setEmail(user.email || "");
    supabase.from("profiles").select("name,language,email_preferences,notification_preferences").eq("id", user.id).maybeSingle().then(({ data }) => {
      if (data) {
        setName(data.name || "");
        setPrefs({ ...DEFAULT_PREFS, ...((data.email_preferences as EmailPrefs | null) ?? {}) });
        setNotifPrefs({ ...DEFAULT_NOTIF_PREFS, ...((data.notification_preferences as Partial<NotifPrefs> | null) ?? {}) });
      }
    });
  }, [user]);

  const save = async () => {
    const { error } = await supabase.from("profiles").update({ name }).eq("id", user!.id);
    if (error) toast.error(error.message); else toast.success(t("profile.profileSaved"));
  };

  const changeLang = async (code: LangCode) => {
    await i18n.changeLanguage(code);
    localStorage.setItem("lang", code);
    if (user) await supabase.from("profiles").update({ language: code }).eq("id", user.id);
  };

  const togglePref = async (key: keyof EmailPrefs, value: boolean) => {
    const next = { ...prefs, [key]: value };
    setPrefs(next);
    if (!user) return;
    const { error } = await supabase.from("profiles").update({ email_preferences: next }).eq("id", user.id);
    if (error) { toast.error(error.message); setPrefs(prefs); }
  };

  const toggleNotifPref = async (key: keyof NotifPrefs, value: boolean) => {
    const next = { ...notifPrefs, [key]: value };
    setNotifPrefs(next);
    if (!user) return;
    const { error } = await supabase.from("profiles").update({ notification_preferences: next }).eq("id", user.id);
    if (error) { toast.error(error.message); setNotifPrefs(notifPrefs); }
  };

  return (
    <AppShell title={t("profile.title")}>
      <PageMeta
        title="Profil — Tipomatic"
        description="Spravuj svůj profil, jazyk a notifikace v Tipomaticu."
        path="/profile"
      />
      <h1 className="text-2xl font-bold mb-4">{t("profile.title")}</h1>
      <div className="space-y-4">
        <Card>
          <CardHeader><CardTitle className="text-base">{t("profile.account")}</CardTitle></CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2"><Label>{t("profile.displayName")}</Label><Input value={name} onChange={e => setName(e.target.value)} className="h-11" /></div>
            <div className="space-y-2"><Label>{t("auth.email")}</Label><Input value={email} disabled className="h-11" /></div>
            <Button onClick={save} className="w-full h-11 bg-gradient-pitch">{t("profile.saveProfile")}</Button>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base">{t("common.language")}</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <p className="text-sm text-muted-foreground mb-2">{t("profile.languageDesc")}</p>
            {SUPPORTED_LANGUAGES.map(l => (
              <button key={l.code} onClick={() => changeLang(l.code as LangCode)}
                className={`w-full flex items-center justify-between rounded-xl border px-4 py-3 transition ${
                  i18n.resolvedLanguage === l.code ? "border-primary bg-primary/5" : "hover:bg-muted/50"
                }`}>
                <span className="font-medium"><span className="mr-2 text-lg">{l.flag}</span>{l.label}</span>
                {i18n.resolvedLanguage === l.code && <span className="text-primary text-sm font-bold">✓</span>}
              </button>
            ))}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base">{t("profile.theme")}</CardTitle></CardHeader>
          <CardContent className="space-y-2">
            <p className="text-sm text-muted-foreground mb-2">{t("profile.themeDesc")}</p>
            <button
              onClick={useDefaultTheme}
              className={`w-full flex items-center justify-between rounded-xl border px-4 py-3 transition ${
                themeOverride === null ? "border-primary bg-primary/5" : "hover:bg-muted/50"
              }`}
            >
              <span className="font-medium">
                {t("profile.useDefaultTheme")}
                <span className="ml-2 text-xs text-muted-foreground">({t(APP_THEMES.find(x => x.id === defaultTheme)?.labelKey ?? "")})</span>
              </span>
              {themeOverride === null && <Check className="h-4 w-4 text-primary" />}
            </button>
            {APP_THEMES.map((th) => {
              const active = themeOverride === th.id;
              const swatches =
                th.id === "emerald"
                  ? ["hsl(0 0% 100%)", "hsl(145 70% 35%)", "hsl(145 75% 40%)"]
                  : th.id === "greenblack"
                  ? ["hsl(145 80% 42%)", "hsl(150 30% 10%)", "hsl(145 85% 55%)"]
                  : ["hsl(265 60% 22%)", "hsl(338 85% 56%)", "hsl(320 90% 65%)"];
              return (
                <button
                  key={th.id}
                  onClick={() => pickTheme(th.id)}
                  className={`w-full flex items-center justify-between rounded-xl border px-4 py-3 transition ${
                    active ? "border-primary bg-primary/5" : "hover:bg-muted/50"
                  }`}
                >
                  <span className="flex items-center gap-3">
                    <span className="flex gap-1">
                      {swatches.map((c, i) => (
                        <span key={i} className="h-5 w-5 rounded-full border border-border" style={{ background: c }} />
                      ))}
                    </span>
                    <span className="font-medium">{t(th.labelKey)}</span>
                  </span>
                  {active && <Check className="h-4 w-4 text-primary" />}
                </button>
              );
            })}
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base">{t("notifications.settingsTitle")}</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <p className="text-sm text-muted-foreground">{t("notifications.settingsDesc")}</p>
            <div className="flex items-center justify-between">
              <div className="pr-3">
                <p className="text-sm font-medium">{t("notifications.prefTipReminder")}</p>
                <p className="text-xs text-muted-foreground">{t("notifications.prefTipReminderDesc")}</p>
              </div>
              <Switch checked={notifPrefs.tip_reminder} onCheckedChange={(v) => toggleNotifPref("tip_reminder", v)} />
            </div>
            <div className="flex items-center justify-between">
              <div className="pr-3">
                <p className="text-sm font-medium">{t("notifications.prefMatchResult")}</p>
                <p className="text-xs text-muted-foreground">{t("notifications.prefMatchResultDesc")}</p>
              </div>
              <Switch checked={notifPrefs.match_result} onCheckedChange={(v) => toggleNotifPref("match_result", v)} />
            </div>
            <div className="flex items-center justify-between">
              <div className="pr-3">
                <p className="text-sm font-medium">{t("notifications.prefInvite")}</p>
                <p className="text-xs text-muted-foreground">{t("notifications.prefInviteDesc")}</p>
              </div>
              <Switch checked={notifPrefs.invite} onCheckedChange={(v) => toggleNotifPref("invite", v)} />
            </div>
            <div className="flex items-center justify-between">
              <div className="pr-3">
                <p className="text-sm font-medium">{t("notifications.prefRulesChange")}</p>
                <p className="text-xs text-muted-foreground">{t("notifications.prefRulesChangeDesc")}</p>
              </div>
              <Switch checked={notifPrefs.rules_change} onCheckedChange={(v) => toggleNotifPref("rules_change", v)} />
            </div>
            <div className="border-t pt-3 mt-1">
              <div className="flex items-center justify-between">
                <div className="pr-3">
                  <p className="text-sm font-medium">{t("notifications.pushTitle")}</p>
                  <p className="text-xs text-muted-foreground">
                    {pushSupported ? t("notifications.pushDesc") : t("notifications.pushUnsupported")}
                  </p>
                </div>
                <div className="flex items-center gap-2">
                  {pushBusy && <Loader2 className="h-4 w-4 animate-spin text-muted-foreground" />}
                  <Switch
                    checked={pushActive && notifPrefs.push_enabled}
                    disabled={!pushSupported || pushBusy}
                    onCheckedChange={togglePush}
                  />
                </div>
              </div>
            </div>
            <Button
              type="button"
              variant="outline"
              className="w-full h-10"
              disabled={testBusy}
              onClick={sendTest}
            >
              {testBusy ? t("common.loading", { defaultValue: "..." }) : t("notifications.sendTest")}
            </Button>
          </CardContent>
        </Card>
        <Card>
          <CardHeader><CardTitle className="text-base">{t("profile.emailNotifications", { defaultValue: "E-mailové notifikace" })}</CardTitle></CardHeader>
          <CardContent className="space-y-3">
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">{t("profile.emailInvites", { defaultValue: "Pozvánky do ligy" })}</p>
                <p className="text-xs text-muted-foreground">{t("profile.emailInvitesDesc", { defaultValue: "Když tě někdo pozve do ligy." })}</p>
              </div>
              <Switch checked={prefs.invites} onCheckedChange={(v) => togglePref("invites", v)} />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">{t("profile.emailResults", { defaultValue: "Výsledky zápasů" })}</p>
                <p className="text-xs text-muted-foreground">{t("profile.emailResultsDesc", { defaultValue: "Po každém zápase souhrn tvého tipu a získaných bodů." })}</p>
              </div>
              <Switch checked={prefs.results} onCheckedChange={(v) => togglePref("results", v)} />
            </div>
            <div className="flex items-center justify-between">
              <div>
                <p className="text-sm font-medium">{t("profile.emailChanges", { defaultValue: "Změny v lize a zápasech" })}</p>
                <p className="text-xs text-muted-foreground">{t("profile.emailChangesDesc", { defaultValue: "Změna pravidel ligy nebo přesun zápasu." })}</p>
              </div>
              <Switch checked={prefs.changes} onCheckedChange={(v) => togglePref("changes", v)} />
            </div>
          </CardContent>
        </Card>
        <Button variant="outline" className="w-full h-11" onClick={async () => { await supabase.auth.signOut(); }}>
          {t("common.signOut")}
        </Button>
      </div>
    </AppShell>
  );
};

export default Profile;
